import type { TypeSelectOption } from '@thefarmersfront/kitchen';

import { EXTERNAL_URLS } from '~/constants/externalUrls';

/**
 * [배송대행 관리] 페이지네이션 관련 상수
 */
export const INITIAL_DELIVERY_AGENCY_PAGE_SIZE = 50;
export const INITIAL_DELIVERY_AGENCY_PAGE_CURSOR = 1;

export const DELIVERY_AGENCY_PAGE_SIZE = {
  50: {
    value: 50,
    text: '50개',
  },
  100: {
    value: 100,
    text: '100개',
  },
  200: {
    value: 200,
    text: '200개',
  },
} as const;

export type TypeClaimPageSize = keyof typeof DELIVERY_AGENCY_PAGE_SIZE;

/**
 * [배송대행 관리][운송장 관리] 페이지네이션 사이즈 옵션 (운송장 관리 전용)
 */
export const DELIVERY_AGENCY_TRACKING_PAGE_SIZE = {
  ...DELIVERY_AGENCY_PAGE_SIZE,
  300: {
    value: 300,
    text: '300개',
  },
  500: {
    value: 500,
    text: '500개',
  },
} as const;

/**
 * [배송대행 관리] 날짜 검색 최대 기간
 */
export const MAX_DELIVERY_AGENCY_DATE_RANGE = 31;

/**
 * [배송대행 관리] 상품명 검색 키워드 타입 코드
 * 상품명 키워드 선택 시 배송 예정일을 필수로 강제하기 위한 분기 기준값
 */
export const DELIVERY_AGENCY_PRODUCT_NAME_KEYWORD_TYPE = 'PRODUCT_NAME';

/**
 * [배송대행 관리] 툴팁 안내 문구
 */
export const DELIVERY_AGENCY_TOOLTIP_MESSAGE = {
  OUTBOUND_UNIT_CONDITION:
    '배송예정일, 수령자, 수령자 연락처, 도로명 기본주소, 상세주소, 수령장소, 수령장소 상세, 출입방법, 출입방법 상세, 배송사, 배송유형까지 모두 일치하는 주문은 하나로 묶어서 포장할 수 있습니다.',
  SEND_ALIM_TALK_IMMEDIATELY:
    "해당 주문의 알림톡 즉시 발송 여부와 상관 없이\n출고번호가 같은 다른 주문이 알림톡 즉시 발송 'Y' 로 설정되어 있으면 배송완료 직후 알림톡이 발송됩니다.",
} as const;

/**
 * [배송대행 관리] 주문 상태
 * TODO - 출고완료 추후에 추가 (오픈스펙 아님)
 */
export const DELIVERY_AGENCY_ORDER_STATUS = {
  OUTBOUND_READY: 'OUTBOUND_READY',
  OUTBOUND_PROCESSING: 'OUTBOUND_PROCESSING',
  ON_THE_WAY: 'ON_THE_WAY',
  DELIVERY_COMPLETED: 'DELIVERY_COMPLETED',
  CANCELED: 'CANCELED',
  DELIVERY_FAILED: 'DELIVERY_FAILED',
} as const;

export type TypeDeliveryAgencyOrderStatus =
  (typeof DELIVERY_AGENCY_ORDER_STATUS)[keyof typeof DELIVERY_AGENCY_ORDER_STATUS];

/**
 * [배송대행 관리] 주문 상태 뱃지 색상 매핑
 */
export const DELIVERY_AGENCY_ORDER_STATUS_BADGE_COLOR = {
  [DELIVERY_AGENCY_ORDER_STATUS.OUTBOUND_READY]: 'secondary',
  [DELIVERY_AGENCY_ORDER_STATUS.OUTBOUND_PROCESSING]: 'info',
  [DELIVERY_AGENCY_ORDER_STATUS.ON_THE_WAY]: 'info',
  [DELIVERY_AGENCY_ORDER_STATUS.DELIVERY_COMPLETED]: 'success',
  [DELIVERY_AGENCY_ORDER_STATUS.CANCELED]: 'error',
  [DELIVERY_AGENCY_ORDER_STATUS.DELIVERY_FAILED]: 'warning',
} as const;

/**
 * [배송대행 관리] 운송장 상태
 */
export const DELIVERY_AGENCY_SHIPPING_LABEL_STATUS = {
  OUTBOUND_READY: 'OUTBOUND_READY',
  OUTBOUND_PROCESSING: 'OUTBOUND_PROCESSING',
  ON_THE_WAY: 'ON_THE_WAY',
  DELIVERY_COMPLETED: 'DELIVERY_COMPLETED',
  DELIVERY_CANCELED: 'DELIVERY_CANCELED',
  DELIVERY_FAILED: 'DELIVERY_FAILED',
} as const;

export type TypeDeliveryAgencyShippingLabelStatus =
  (typeof DELIVERY_AGENCY_SHIPPING_LABEL_STATUS)[keyof typeof DELIVERY_AGENCY_SHIPPING_LABEL_STATUS];

/**
 * [배송대행 관리] 운송장 상태 뱃지 색상 매핑
 */
export const DELIVERY_AGENCY_SHIPPING_LABEL_STATUS_BADGE_COLOR = {
  [DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.OUTBOUND_READY]: 'secondary',
  [DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.OUTBOUND_PROCESSING]: 'info',
  [DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.ON_THE_WAY]: 'info',
  [DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.DELIVERY_COMPLETED]: 'success',
  [DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.DELIVERY_CANCELED]: 'error',
  [DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.DELIVERY_FAILED]: 'warning',
} as const;

/**
 * [배송대행관리] 배송사 코드 매핑
 */
export const DELIVERY_AGENCY_COURIER_CODE = {
  CJT: {
    value: 'CJT',
    text: 'CJ',
  },
  NM: {
    value: 'NM',
    text: '넥스트마일',
  },
  LTT: {
    value: 'LTT',
    text: '롯데',
  },
} as const;

export const DELIVERY_AGENCY_TRACKING_LINKS = {
  [DELIVERY_AGENCY_COURIER_CODE.CJT.value]: EXTERNAL_URLS.DELIVERY_AGENCY_TRACKING_LINK_CJT,
  [DELIVERY_AGENCY_COURIER_CODE.NM.value]: EXTERNAL_URLS.DELIVERY_AGENCY_TRACKING_LINK_KURLY_NEXT_MILE,
  [DELIVERY_AGENCY_COURIER_CODE.LTT.value]: EXTERNAL_URLS.DELIVERY_AGENCY_TRACKING_LINK_LTT,
} as const;

export type TypeDeliveryAgencyCourierCode = keyof typeof DELIVERY_AGENCY_COURIER_CODE;

/**
 * [배송대행관리][운송장관리] 운송장 출력
 */
export const DELIVERY_AGENCY_TRACKING_PRINT_STATUS = {
  Y: { value: 'Y', text: '출력함' },
  N: { value: 'N', text: '출력안함' },
} as const;

export type TypeDeliveryAgencyTrackingPrintStatus =
  (typeof DELIVERY_AGENCY_TRACKING_PRINT_STATUS)[keyof typeof DELIVERY_AGENCY_TRACKING_PRINT_STATUS]['value'];

/**
 * [운송장 관리] 추가 운송장 1회 최대 생성 수량
 */
export const MAX_SHIPPING_LABEL_QUANTITY = 30;

/**
 * [배송 결과 리포트] 배송 유형 코드
 */
export const DELIVERY_AGENCY_DELIVERY_TYPE = {
  DAWN: 'DAWN',
  DAY: 'DAY',
} as const;

/**
 * [배송 결과 리포트] 화주사 노출 운송장 상태
 */
export const DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS = {
  OUTBOUND_READY_OR_PROCESSING: 'OUTBOUND_READY_OR_PROCESSING',
  ON_THE_WAY: 'ON_THE_WAY',
  DELIVERY_COMPLETED: 'DELIVERY_COMPLETED',
  DELIVERY_CANCELED: 'DELIVERY_CANCELED',
  DELIVERY_FAILED: 'DELIVERY_FAILED',
} as const;

/**
 * [배송 결과 리포트] 서머리 '진행 중' 카드 구성 상태 코드
 */
export const DELIVERY_AGENCY_REPORT_IN_PROGRESS_STATUS_CODES = [
  DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.OUTBOUND_READY_OR_PROCESSING,
  DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.ON_THE_WAY,
] as const;

/**
 * [배송 결과 리포트] 화주사 노출 운송장 상태 뱃지 색상 매핑
 */
export const DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS_BADGE_COLOR = {
  [DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.OUTBOUND_READY_OR_PROCESSING]: 'info',
  [DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.ON_THE_WAY]: 'info',
  [DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.DELIVERY_COMPLETED]: 'success',
  [DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.DELIVERY_CANCELED]: 'secondary',
  [DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.DELIVERY_FAILED]: 'warning',
} as const;

/**
 * [배송 결과 리포트] 화주사 노출 배송 결과
 */
export const DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT = {
  NORMAL: { value: 'NORMAL', text: '정상배송' },
  DELAYED: { value: 'DELAYED', text: '지연배송' },
  NON_DELIVERED: { value: 'NON_DELIVERED', text: '미배송' },
  ALTERNATIVE_DELIVERY: { value: 'ALTERNATIVE_DELIVERY', text: '대응배송' },
  CONVERTED_DELIVERY: { value: 'CONVERTED_DELIVERY', text: '전환배송' },
} as const;

/**
 * [배송 결과 리포트] 화주사 노출 배송 결과 뱃지 색상 매핑
 */
export const DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT_BADGE_COLOR = {
  [DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT.NORMAL.value]: 'secondary',
  [DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT.ALTERNATIVE_DELIVERY.value]: 'secondary',
  [DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT.DELAYED.value]: 'warning',
  [DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT.CONVERTED_DELIVERY.value]: 'warning',
  [DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT.NON_DELIVERED.value]: 'error',
} as const;

export const DELIVERY_AGENCY_SORT_BY = {
  CREATED_AT_DESC: 'CREATED_AT_DESC',
  CREATED_AT_ASC: 'CREATED_AT_ASC',
} as const;

export type TypeDeliveryAgencySortBy = (typeof DELIVERY_AGENCY_SORT_BY)[keyof typeof DELIVERY_AGENCY_SORT_BY];

export const DELIVERY_AGENCY_ORDER_SORT_OPTIONS: TypeSelectOption<TypeDeliveryAgencySortBy>[] = [
  { value: DELIVERY_AGENCY_SORT_BY.CREATED_AT_DESC, text: '등록일 (내림차순)' },
  { value: DELIVERY_AGENCY_SORT_BY.CREATED_AT_ASC, text: '등록일 (오름차순)' },
];

export const DELIVERY_AGENCY_CREATED_SORT_OPTIONS: TypeSelectOption<TypeDeliveryAgencySortBy>[] = [
  { value: DELIVERY_AGENCY_SORT_BY.CREATED_AT_DESC, text: '생성일 (내림차순)' },
  { value: DELIVERY_AGENCY_SORT_BY.CREATED_AT_ASC, text: '생성일 (오름차순)' },
];

/** [회수 신청/조회] 반품요청번호 상태 */
export const COLLECTING_REQUEST_STATUS = {
  COLLECT_ACCEPTED: { value: 'COLLECT_ACCEPTED', text: '회수 예정' },
  COLLECT_DEPARTURE_READY: { value: 'COLLECT_DEPARTURE_READY', text: '회수 출발 준비' },
  COLLECT_ON_THE_WAY: { value: 'COLLECT_ON_THE_WAY', text: '회수 중' },
  COLLECT_PARTIAL: { value: 'COLLECT_PARTIAL', text: '회수 완료(부분)' },
  COLLECT_COMPLETED: { value: 'COLLECT_COMPLETED', text: '회수 완료' },
  COLLECT_FAILED: { value: 'COLLECT_FAILED', text: '회수 실패' },
  COLLECT_CANCELED: { value: 'COLLECT_CANCELED', text: '회수 취소' },
} as const;

/** [회수 신청/조회] 반품요청번호 상태 뱃지 색상 매핑 */
export const COLLECTING_REQUEST_STATUS_BADGE_COLOR = {
  [COLLECTING_REQUEST_STATUS.COLLECT_ACCEPTED.value]: 'secondary',
  [COLLECTING_REQUEST_STATUS.COLLECT_DEPARTURE_READY.value]: 'primary',
  [COLLECTING_REQUEST_STATUS.COLLECT_ON_THE_WAY.value]: 'info',
  [COLLECTING_REQUEST_STATUS.COLLECT_PARTIAL.value]: 'mint',
  [COLLECTING_REQUEST_STATUS.COLLECT_COMPLETED.value]: 'success',
  [COLLECTING_REQUEST_STATUS.COLLECT_FAILED.value]: 'error',
  [COLLECTING_REQUEST_STATUS.COLLECT_CANCELED.value]: 'warning',
} as const;

/** [회수 신청/조회] 회수 운송장 상태 뱃지 색상 매핑 */
/** [회수 결과 리포트] 회수 운송장 상태 — 반품요청번호 상태와 달리 COLLECT_PARTIAL이 없다 */
export const COLLECTING_SHIPPING_LABEL_STATUS = {
  COLLECT_ACCEPTED: { value: 'COLLECT_ACCEPTED', text: '회수 예정' },
  COLLECT_DEPARTURE_READY: { value: 'COLLECT_DEPARTURE_READY', text: '회수 출발 준비' },
  COLLECT_ON_THE_WAY: { value: 'COLLECT_ON_THE_WAY', text: '회수 중' },
  COLLECT_COMPLETED: { value: 'COLLECT_COMPLETED', text: '회수 완료' },
  COLLECT_FAILED: { value: 'COLLECT_FAILED', text: '회수 실패' },
  COLLECT_CANCELED: { value: 'COLLECT_CANCELED', text: '회수 취소' },
} as const;

export const COLLECTING_SHIPPING_LABEL_STATUS_BADGE_COLOR = {
  COLLECT_ACCEPTED: 'secondary',
  COLLECT_DEPARTURE_READY: 'primary',
  COLLECT_ON_THE_WAY: 'info',
  COLLECT_COMPLETED: 'success',
  COLLECT_FAILED: 'error',
  COLLECT_CANCELED: 'warning',
} as const;

/** 배송 주문 상세 → 배송 주문 반품 신청/조회 딥링크 쿼리 파라미터 */
export const COLLECTING_REQUEST_LINK_PARAM = {
  OUTBOUND_CODE: 'outboundCode',
  DATE_FROM: 'collectingRequestedDateFrom',
  DATE_TO: 'collectingRequestedDateTo',
} as const;

/** 딥링크 진입 시 사용하는 검색 키워드 타입 (출고번호) */
export const COLLECTING_REQUEST_OUTBOUND_KEYWORD_TYPE = 'OUTBOUND_CODE' as const;

/** [반품 신청 2step] 회수한 상품 처리 방식 코드 — 옵션 목록은 API를 쓰고 코드값 좁히기에만 사용한다 */
export const COLLECTED_PRODUCT_HANDLING_CODES = [{ value: 'DISPOSE' }, { value: 'RETURN_TO_SENDER' }] as const;

/** [반품 신청 2step] 회수할 박스 개수 옵션 (선택 없음이 default) */
export const COLLECTING_REQUEST_BOX_COUNT_OPTIONS: TypeSelectOption<number>[] = Array.from(
  { length: 10 },
  (_, index) => ({ value: index + 1, text: String(index + 1) })
);

/** [반품 신청 2step] 고객 연락처 형식 — 전화번호·핸드폰·안심번호를 모두 포함한다 (하이픈 필수) */
export const COLLECTING_REQUEST_CONTACT_TEL_PATTERN = /^\d{2,4}-\d{3,4}-\d{4}$/;

/** [반품 신청 2step] 고객 연락처 최대 입력 길이 */
export const COLLECTING_REQUEST_CONTACT_TEL_MAX_LENGTH = 20;

/** [반품 신청 2step] 상품회수장소 상세·출입방법 상세 최대 입력 길이 */
export const COLLECTING_REQUEST_DETAIL_MAX_LENGTH = 100;

/** [반품 신청 2step] 회수지 상세주소 최대 입력 길이 */
export const COLLECTING_REQUEST_SECONDARY_ADDRESS_MAX_LENGTH = 100;

/** [반품 신청 2step] 회수할 상품명 최대 입력 길이 */
export const COLLECTING_REQUEST_PRODUCT_NAME_MAX_LENGTH = 500;

/** [반품 신청 1step] 후보 원주문 검색의 고정 조건 */
export const COLLECTING_CANDIDATE_COURIER_CODE = 'NM' as const;
export const COLLECTING_CANDIDATE_ORDER_STATUS = 'DELIVERY_COMPLETED' as const;

export const COLLECTING_CANDIDATE_FIXED_COURIER_OPTION: TypeSelectOption<string> = {
  value: COLLECTING_CANDIDATE_COURIER_CODE,
  text: DELIVERY_AGENCY_COURIER_CODE.NM.text,
};

export const COLLECTING_CANDIDATE_FIXED_ORDER_STATUS_OPTION: TypeSelectOption<string> = {
  value: COLLECTING_CANDIDATE_ORDER_STATUS,
  text: '배송 완료',
};

/** [반품 신청/조회] 회수 운송장번호 헤더 툴팁 — 운송장은 등록 후 순차 발급되어 비어 보일 수 있다 */
export const COLLECTING_SHIPPING_LABEL_NUMBER_TOOLTIP =
  '회수 운송장은 반품 등록 후 순차적으로 발급됩니다. 운송장 번호가 보이지 않더라도 정상 접수된 상태이니 안심하셔도 됩니다.';

/** [반품 취소] 결과 얼럿 문구 — 정책서 '반품 취소' 플로우 기준. 회수 예정 상태만 취소 가능하다 */
export const COLLECTING_REQUEST_CANCEL_MESSAGE = {
  ALL_FAILED: (totalCount: number) =>
    `선택한 ${totalCount}개 모두 회수를 시작했거나, 이미 취소된 반품요청번호입니다.\n반품 취소 불가합니다.\n확인 후 다시 시도해주세요.`,
  PARTIAL_FAILED: (successCount: number) =>
    `회수를 시작했거나, 이미 취소된 반품요청번호는 회수 취소 불가합니다.\n불가 건 제외후 ${successCount}개만 반품 취소를 진행했습니다.`,
} as const;

/** [반품 신청 1step] 배송예정일을 필수로 강제하는 키워드 타입 (이름 검색은 범위가 넓어서) */
export const COLLECTING_CANDIDATE_DATE_REQUIRED_KEYWORD_TYPES = ['RECEIVER_NAME', 'ORDERER_NAME'];

/**
 * [배송 주문 관리][주문 상세] 수령장소 코드
 */
export const DELIVERY_AGENCY_DELIVERY_LOCATION = {
  DOORSTEP: { value: 'DOORSTEP', text: '문 앞' },
  DELIVERY_BOX: { value: 'DELIVERY_BOX', text: '택배 수령실' },
  PUBLIC_DOOR: { value: 'PUBLIC_DOOR', text: '공동현관(대문) 앞' },
  ETC: { value: 'ETC', text: '기타' },
} as const;

export type TypeDeliveryAgencyDeliveryLocation =
  (typeof DELIVERY_AGENCY_DELIVERY_LOCATION)[keyof typeof DELIVERY_AGENCY_DELIVERY_LOCATION]['value'];

export const DELIVERY_AGENCY_DELIVERY_LOCATION_OPTIONS: TypeSelectOption<TypeDeliveryAgencyDeliveryLocation>[] =
  Object.values(DELIVERY_AGENCY_DELIVERY_LOCATION);

/** 수령장소 상세가 조건부 필수가 되는 수령장소 코드 (택배 수령실 / 기타) */
export const DELIVERY_AGENCY_DELIVERY_LOCATION_DETAIL_REQUIRED_CODES: TypeDeliveryAgencyDeliveryLocation[] = [
  DELIVERY_AGENCY_DELIVERY_LOCATION.DELIVERY_BOX.value,
  DELIVERY_AGENCY_DELIVERY_LOCATION.ETC.value,
];

/**
 * [배송 주문 관리][주문 상세] 출입방법 코드
 */
export const DELIVERY_AGENCY_ENTRANCE_METHOD = {
  PUBLIC_DOOR_PWD: { value: 'PUBLIC_DOOR_PWD', text: '공동현관 비밀번호' },
  CALL_SECURITY: { value: 'CALL_SECURITY', text: '경비실 호출' },
  FREE_ACCESS: { value: 'FREE_ACCESS', text: '자유출입' },
  ETC: { value: 'ETC', text: '기타' },
} as const;

export type TypeDeliveryAgencyEntranceMethod =
  (typeof DELIVERY_AGENCY_ENTRANCE_METHOD)[keyof typeof DELIVERY_AGENCY_ENTRANCE_METHOD]['value'];

export const DELIVERY_AGENCY_ENTRANCE_METHOD_OPTIONS: TypeSelectOption<TypeDeliveryAgencyEntranceMethod>[] =
  Object.values(DELIVERY_AGENCY_ENTRANCE_METHOD);

/** 출입방법 상세가 조건부 필수가 되는 출입방법 코드 (공동현관 비밀번호 / 기타) */
export const DELIVERY_AGENCY_ENTRANCE_METHOD_DETAIL_REQUIRED_CODES: TypeDeliveryAgencyEntranceMethod[] = [
  DELIVERY_AGENCY_ENTRANCE_METHOD.PUBLIC_DOOR_PWD.value,
  DELIVERY_AGENCY_ENTRANCE_METHOD.ETC.value,
];

/** 수령장소 상세 / 출입방법 상세 최대 입력 길이 */
export const DELIVERY_AGENCY_LOCATION_DETAIL_MAX_LENGTH = 100;
